import React from 'react';
import PropTypes from 'prop-types';
import {Map, List, Set} from 'immutable';
import './ExecCoachingManagerRollup.styl';
import AdminDashCoachingPlanRow from 'components/dashboard/admin/AdminDashCoachingPlanRow';
import moment from 'moment';
import AdminDashInactivePlanRow from 'components/dashboard/admin/AdminDashInactivePlanRow';
import withinDateRange from 'utils/withinDateRange';
import BasedOnDatesLink from 'components/dashboard/common/BasedOnDatesLink';

export default class ExecCoachingManagerRollup extends React.PureComponent {
  render() {
    const {
      startDate,
      endDate,
      users,
      teamManagerIds,
      planMap,
      lastCompletePlan,
      overduePlans,
      adminId,
      fromPath,
      reviewTypes,
      selectedCoachId,
    } = this.props;

    const allPlans = planMap.toList();

    // Filter out any reviews which dont fall within our date range
    const allCompletedReviewsInRange = allPlans
      .flatMap(p => p.reviews)
      .filter(
        review => review.completedAt && withinDateRange([startDate, endDate], review.completedAt)
      );

    const endDateAWeekBack = endDate.clone().subtract(1, 'week');

    function isActivePlan(plan) {
      // a plan is active if
      // 1. it is created in the last 7 days (ending in endDate), or
      // 2. if it has at least one completed review in the date range
      if (
        endDateAWeekBack.isBefore(plan.created) &&
        // this second check is probably redundant because the BE query should have taken care of that for us
        plan.created.isBefore(endDate)
      ) {
        return true;
      }
      const completedReviewsInRange = plan.reviews.filter(
        r =>
          r.status === 'COMPLETED' &&
          r.completedAt &&
          withinDateRange([startDate, endDate], r.completedAt)
      );
      return !completedReviewsInRange.isEmpty();
    }

    const [activeProps, inactiveProps] = users.reduce(
      ([active, inactive], user) => {
        // for this user find all plans, reviews, and overdue plans
        const plans = allPlans.filter(p => p.coach.id === user.id && isActivePlan(p));
        const reviews = allCompletedReviewsInRange.filter(r => r.coachUserId === user.id);
        const overdue = overduePlans.get(user.id) || List();

        if (plans.isEmpty() && reviews.isEmpty()) {
          // if the user has no plans or reviews in the range, add them to the inactive set
          const lastPlan = lastCompletePlan.get(user.id);
          return [
            active,
            teamManagerIds.contains(user.id)
              ? inactive.push({user, lastPlan, overdue}) // lastPlan can be nil
              : inactive,
          ];
        } else {
          // otherwise add them to the active set
          return [
            active.push({
              user,
              plans,
              reviews,
              overdue,
              startDate,
              endDate,
            }),
            inactive,
          ];
        }
      },
      [List(), List()]
    );

    return (
      <div className="dashv2-widget admin-dash-coaching-plans">
        <div className="has-coaching-plans">
          <div className="title-and-dates">
            <h2 className="widget-title dashv2-title">
              Managers With Coaching Activity During Period
            </h2>
            <BasedOnDatesLink
              startDate={this.props.headerStartDate}
              endDate={this.props.headerEndDate}
              location={this.props.location}
            />
          </div>
          <table>
            <thead>
              <tr>
                <th>Coach</th>
                <th>Active Plans</th>
                <th>Completed Plans</th>
                <th>Overdue Plans</th>
                <th>Completed Meetings</th>
              </tr>
            </thead>
            <tbody>
              {activeProps.isEmpty() ? (
                <tr className="empty">
                  <td colSpan={5}>No coaches had coaching activity during this period.</td>
                </tr>
              ) : (
                activeProps
                  .sort((l, r) => l.user.getFullName().localeCompare(r.user.getFullName()))
                  .map((props, i) => (
                    <AdminDashCoachingPlanRow
                      {...props}
                      key={`plan-row-${i}`}
                      index={i}
                      selected={props.user.id === selectedCoachId}
                      adminId={adminId}
                      fromPath={fromPath}
                      users={users}
                      allPlans={allPlans}
                      reviewTypes={reviewTypes}
                    />
                  ))
              )}
            </tbody>
          </table>
        </div>
        <div className="no-active-coaching-plans">
          <div className="title-and-dates">
            <h2 className="widget-title dashv2-title">Managers With No Activity</h2>
            <BasedOnDatesLink {...this.props} />
          </div>
          <table>
            <thead className="inactive-header">
              <tr>
                <th>Coach</th>
                <th className="last-plan-header">Most Recent Plan</th>
                <th className="inactive-overdue-plan-header">Overdue Plans</th>
              </tr>
            </thead>
            <tbody>
              {inactiveProps.isEmpty() ? (
                <tr className="empty">
                  <td colSpan={3}>All coaches have active plans.</td>
                </tr>
              ) : (
                inactiveProps
                  .sort((l, r) => l.user.getFullName().localeCompare(r.user.getFullName()))
                  .map((props, i) => (
                    <AdminDashInactivePlanRow
                      {...props}
                      key={`inactive-row-${i}`}
                      index={i}
                      selected={props.user.id === selectedCoachId}
                      adminId={adminId}
                      fromPath={fromPath}
                    />
                  ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

ExecCoachingManagerRollup.propTypes = {
  startDate: PropTypes.instanceOf(moment),
  endDate: PropTypes.instanceOf(moment),
  users: PropTypes.instanceOf(List), // List<User>
  teamManagerIds: PropTypes.instanceOf(Set), // Set<UserId>
  planMap: PropTypes.instanceOf(Map), // Map<UserId, List<CoachingPlan>>
  lastCompletePlan: PropTypes.instanceOf(Map), // Map<UserId, CoachingPlan>
  reviewTypes: PropTypes.instanceOf(Map), // Map<CoachingReviewTypeId, CoachingReviewType>
};

ExecCoachingManagerRollup.defaultProps = {
  users: List(),
  planMap: Map(),
  lastCompletePlan: Map(),
};
